import { useEffect } from 'react'

import { Box, IconButton, useBreakpointValue } from '@chakra-ui/react'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { AnimatePresence, motion } from 'framer-motion'
import _ from 'lodash'
import { ItList } from 'types/graphql'

import { Form, useFormContext } from '@redwoodjs/forms'

import BrandButton from 'src/components/widgets/buttons/BrandButton/BrandButton'
import HoneyPot from 'src/components/widgets/HoneyPot/HoneyPot'
import Icon from 'src/components/widgets/Icon/Icon'
import Input from 'src/components/widgets/Input/Input'
import { EMAIL_VALIDATION, LOCAL_STORAGE_REFERRAL_KEY } from 'src/lib/constants'
import type { RegistrationFormType } from 'src/lib/hooks/auth/useUserRegistration'
import { get } from 'src/lib/storedAffiliations'
import { BrandColor } from 'src/types'

import useUnlockWithEmail from './useUnlockWithEmail'

const UnlockWithEmail: React.FC<{
  itListId: number
  title: string
  creator: ItList['creator']
  emailElRef: React.MutableRefObject<HTMLInputElement>
  brandColor: BrandColor
}> = ({ brandColor, emailElRef, ...props }) => {
  const referrerId = get(LOCAL_STORAGE_REFERRAL_KEY)
  const { onSubmit, errorMessage, loading, showSuccessOverlay, onFocus } =
    useUnlockWithEmail(props)
  return (
    <Form<RegistrationFormType>
      onSubmit={onSubmit}
      style={{ width: '100%' }}
      noValidate
      config={{ defaultValues: { referrerId } }}
    >
      <FormInner
        brandColor={brandColor}
        emailElRef={emailElRef}
        errorMessage={errorMessage}
        loading={loading}
        showSuccessOverlay={showSuccessOverlay}
        onFocus={onFocus}
      />
    </Form>
  )
}

const FormInner: React.FC<{
  brandColor: BrandColor
  emailElRef: React.MutableRefObject<HTMLInputElement>
  showSuccessOverlay: boolean
  errorMessage?: string
  loading?: boolean
  onFocus: React.FocusEventHandler
}> = ({
  brandColor,
  emailElRef,
  showSuccessOverlay,
  errorMessage,
  loading,
  onFocus,
}) => {
  const { setError, clearErrors } = useFormContext()

  useEffect(() => {
    if (errorMessage) {
      setError('email', {
        type: 'custom',
        message: errorMessage,
      })
    } else {
      clearErrors('email')
    }
  }, [errorMessage, setError, clearErrors])
  const isSm = useBreakpointValue({ sm: true, md: false })
  if (isSm === undefined) return null

  return (
    <Box position="relative" w="100%">
      <HoneyPot />
      <Input
        ref={emailElRef}
        name="email"
        type="email"
        autoComplete="email"
        variant="round"
        borderColor={brandColor}
        hideLabel={showSuccessOverlay}
        onFocus={onFocus}
        _focusVisible={{ borderColor: brandColor }}
        _hover={{ borderColor: brandColor }}
        _invalid={{ borderColor: 'danger' }}
        placeholder="Enter your email to get access"
        title="Email"
        validation={EMAIL_VALIDATION}
        pr={10}
        pl={4}
        w="100%"
        data-testid="unlockWithEmail-emailField"
      />
      {isSm && (
        <IconButton
          position="absolute"
          right={2}
          top="7px"
          aria-label="Submit"
          icon={<Icon icon={faArrowRight} />}
          type="submit"
          bg={brandColor}
          color="white"
          py="8px"
          paddingY="8px"
          px="12px"
          paddingX="12px"
          isLoading={loading}
          _hover={{ bg: brandColor }}
          data-testid="unlockWithEmail-accessButton"
        />
      )}
      {!isSm && (
        <BrandButton
          brandColor={brandColor}
          position="absolute"
          right={2}
          top="7px"
          py="8px"
          paddingY="8px"
          px="12px"
          paddingX="12px"
          type="submit"
          isLoading={loading}
          data-testid="unlockWithEmail-accessButton"
        >
          Get Access Link
        </BrandButton>
      )}
      {showSuccessOverlay && (
        <AnimatePresence>
          <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            borderRadius="full"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            p={4}
            bg="successful"
            color="white"
            textAlign="center"
            fontWeight={600}
            data-testid="unlockWithEmail-accessEmailed"
          >
            We emailed your access link!
          </Box>
        </AnimatePresence>
      )}
    </Box>
  )
}

export default UnlockWithEmail
